import VariableService, {
  type VariableEntriesQueryParams,
  type VariableListQueryParams,
} from '@/services/variables';
import { StandardResponse } from '@/types/common';
import type {
  CreateVariablePayload,
  MyVariable,
  UpdateVariablePayload,
  UserVariableUpdatePayload,
  Variable,
} from '@/types/variables';
import { getErrorMessage } from '@/utils/common';
import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { toast } from 'react-hot-toast';

type CreateVariableErrorResponse = {
  response?: {
    data?: {
      errors?: {
        variable_0?: Record<string, string[]>;
      };
    };
  };
};

const getCreateVariableErrorMessage = (error: unknown): string => {
  const fieldErrors =
    (error as CreateVariableErrorResponse)?.response?.data?.errors?.variable_0;

  if (fieldErrors) {
    const firstError = Object.values(fieldErrors)[0];
    if (Array.isArray(firstError) && firstError.length > 0) {
      return firstError[0];
    }
  }

  return 'Failed to create variable';
};

const getMutationErrorData = (error: unknown): unknown =>
  (error as { response?: { data?: unknown } })?.response?.data ?? error;

// Variables Query Keys
export const variablesKeys = {
  all: ['variables'] as const,
  lists: () => [...variablesKeys.all, 'list'] as const,
  list: (filters: VariableListQueryParams) =>
    [...variablesKeys.lists(), { ...filters }] as const,
  details: () => [...variablesKeys.all, 'detail'] as const,
  detail: (id: number) => [...variablesKeys.details(), id] as const,
};

// My Variables Query Keys
export const myVariablesKeys = {
  all: ['my-variables'] as const,
  lists: () => [...myVariablesKeys.all, 'list'] as const,
  list: (filters: VariableListQueryParams) =>
    [...myVariablesKeys.lists(), { ...filters }] as const,
  details: () => [...myVariablesKeys.all, 'detail'] as const,
  detail: (id: number) => [...myVariablesKeys.details(), id] as const,
};

// Variables Hooks
export const useVariables = (filters?: VariableListQueryParams) => {
  return useQuery<StandardResponse<Variable>>({
    queryKey: variablesKeys.list(filters ?? {}),
    queryFn: () => VariableService.getVariables(filters),
  });
};

export const useVariable = (id: number) => {
  return useQuery<Variable>({
    queryKey: variablesKeys.detail(id),
    queryFn: () => VariableService.getVariablesById(id),
  });
};

export const useCreateVariables = () => {
  const queryClient = useQueryClient();

  return useMutation<StandardResponse<Variable>, Error, CreateVariablePayload>({
    mutationFn: (data: CreateVariablePayload) =>
      VariableService.createVariables(data),
    onSuccess: () => {
      toast.success('Variable created successfully');
      queryClient.invalidateQueries({ queryKey: variablesKeys.lists() });
    },
    onError: (error: unknown) => {
      toast.error(getCreateVariableErrorMessage(error));
    },
  });
};

export const useUpdateVariable = (id: number) => {
  const queryClient = useQueryClient();

  return useMutation<Variable, Error, UpdateVariablePayload>({
    mutationFn: (data: UpdateVariablePayload) =>
      VariableService.updateVariables(id, data),
    onSuccess: () => {
      toast.success('Variable updated successfully');
      queryClient.invalidateQueries({ queryKey: variablesKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: variablesKeys.lists() });
    },
    onError: () => {
      toast.error('Failed to update variable');
    },
  });
};

export const useDeleteVariable = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: (id: number) => VariableService.deleteVariables(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: variablesKeys.lists() });
    },
  });
};

// My Variables Hooks
export const prefetchMyVariables = (
  queryClient: QueryClient,
  filters?: VariableListQueryParams,
  options?: { tenantId?: number }
) =>
  queryClient.prefetchQuery({
    queryKey: myVariablesKeys.list(filters ?? {}),
    queryFn: () => VariableService.getMyVariables(filters, options),
  });

export const useMyVariables = (
  filters?: VariableListQueryParams,
  options?: { enabled?: boolean }
) => {
  return useQuery<StandardResponse<MyVariable>>({
    queryKey: myVariablesKeys.list(filters ?? {}),
    queryFn: () => VariableService.getMyVariables(filters),
    enabled: options?.enabled ?? true,
  });
};

export const useMyVariable = (id: number) => {
  return useQuery<MyVariable>({
    queryKey: myVariablesKeys.detail(id),
    queryFn: () => VariableService.getMyVariablesById(id),
  });
};

export const useUpdateMyVariable = () => {
  const queryClient = useQueryClient();

  return useMutation<MyVariable, Error, UserVariableUpdatePayload>({
    mutationFn: (data: UserVariableUpdatePayload) =>
      VariableService.updateUserVariable(data),
    onSuccess: () => {
      toast.success('Profile updated successfully');
      queryClient.invalidateQueries({ queryKey: myVariablesKeys.lists() });
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(getMutationErrorData(error)));
    },
  });
};

export const useTriggerExtraction = () => {
  return useMutation<
    StandardResponse<MyVariable>,
    Error,
    Record<string, number | undefined>
  >({
    mutationFn: (data: Record<string, number | undefined>) =>
      VariableService.triggerExtraction(data),
    onSuccess: () => {},
    onError: () => {},
  });
};

export const useAllVariablesWithValues = (
  data: VariableEntriesQueryParams,
  enabled = true
) => {
  return useQuery<StandardResponse<Variable>>({
    queryKey: ['variables-entries', data],
    queryFn: () => VariableService.getAllVariablesWithValues(data),
    enabled,
  });
};
